<template>
  <div class="options-panel pb-4">
    <!-- Section Toggles -->
    <div class="px-4 pt-4">
      <IncidentReportSectionToggles
        :value="renderOptions"
        @input="onRenderOptionsUpdate"
      />
    </div>

    <hr class="my-0" />

    <!-- People Selector -->
    <div class="px-4 pt-4">
      <IncidentReportPeopleSelector
        :involved-people="involvedPeople"
        :selected-people-ids="renderOptions.includedPeopleIds || []"
        @update:selectedPeopleIds="onPeopleUpdate"
      />
    </div>

    <hr class="my-0" />

    <!-- Claims Selector -->
    <div v-if="eventClaims && eventClaims.length > 0">
      <hr class="my-0" />
      <div class="px-4 pt-4 mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="text-uppercase font-weight-bold text-secondary mb-0">
            <i class="fas fa-file-invoice-dollar mr-1"></i>
            Insurance Claims
            <b-badge variant="secondary" pill class="ml-1">
              {{ (renderOptions.includedInsuranceClaimIds || []).length }} of {{ eventClaims.length }} selected
            </b-badge>
          </h6>
          <div>
            <b-link class="small mr-2" @click="selectAllClaims">Select All</b-link>
            <b-link class="small" @click="deselectAllClaims">Deselect All</b-link>
          </div>
        </div>
        <b-list-group>
          <b-list-group-item
            v-for="claim in eventClaims"
            :key="claim.id"
            class="d-flex justify-content-between align-items-center py-2"
          >
            <div>
              <i class="fas fa-file-invoice-dollar text-secondary mr-2"></i>
              <strong>{{ claim.title || claim.claimNumber || 'Claim' }}</strong>
              <small class="text-muted ml-2" v-if="claim.claimNumber && claim.title">
                #{{ claim.claimNumber }}
              </small>
            </div>
            <b-form-checkbox
              :checked="isClaimSelected(claim.id)"
              size="lg"
              @change="toggleClaim(claim.id)"
            />
          </b-list-group-item>
        </b-list-group>
      </div>
    </div>

    <!-- Expenses Selector -->
    <div v-if="eventExpenses && eventExpenses.length > 0">
      <hr class="my-0" />
      <div class="px-4 pt-4 mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="text-uppercase font-weight-bold text-secondary mb-0">
            <i class="fas fa-receipt mr-1"></i>
            Damage Related Expenses
            <b-badge variant="secondary" pill class="ml-1">
              {{ (renderOptions.includedExpenseIds || []).length }} of {{ eventExpenses.length }} selected
            </b-badge>
          </h6>
          <div>
            <b-link class="small mr-2" @click="selectAllExpenses">Select All</b-link>
            <b-link class="small" @click="deselectAllExpenses">Deselect All</b-link>
          </div>
        </div>
        <b-list-group>
          <b-list-group-item
            v-for="exp in eventExpenses"
            :key="exp.id"
            class="d-flex justify-content-between align-items-center py-2"
          >
            <div>
              <i class="fas fa-receipt text-secondary mr-2"></i>
              <strong>{{ exp.vendorName || exp.otherName || '\u2014' }}</strong>
              <small class="text-muted ml-2">{{ exp.costCategory }} &middot; ${{ Number(exp.amount).toFixed(2) }}</small>
            </div>
            <b-form-checkbox
              :checked="isExpenseSelected(exp.id)"
              size="lg"
              @change="toggleExpense(exp.id)"
            />
          </b-list-group-item>
        </b-list-group>
      </div>
    </div>

    <!-- Media Selector (Images + Videos) -->
    <div class="px-4 pt-4">
      <IncidentReportMediaSelector
        :images="allImages"
        :videos="eventVideos || []"
        :media-loading="mediaLoading"
        :selected-image-ids="renderOptions.includedImageIds || []"
        :selected-video-ids="renderOptions.includedVideoIds || []"
        @update:selectedImageIds="onImagesUpdate"
        @update:selectedVideoIds="onVideosUpdate"
      />
    </div>

    <!-- Documents Selector -->
    <div v-if="nonIncidentReportDocuments.length > 0">
      <hr class="my-0" />
      <div class="px-4 pt-4 mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="text-uppercase font-weight-bold text-secondary mb-0">
            <i class="fas fa-paperclip mr-1"></i>
            Attach Documents at End
            <b-badge variant="secondary" pill class="ml-1">
              {{ (renderOptions.includedDocumentIds || []).length }} of {{ nonIncidentReportDocuments.length }} selected
            </b-badge>
          </h6>
          <div>
            <b-link class="small mr-2" @click="selectAllDocs">Select All</b-link>
            <b-link class="small" @click="deselectAllDocs">Deselect All</b-link>
          </div>
        </div>
        <b-list-group>
          <b-list-group-item
            v-for="doc in nonIncidentReportDocuments"
            :key="doc.id"
            class="d-flex justify-content-between align-items-center py-2"
          >
            <div>
              <i class="fas fa-file text-secondary mr-2"></i>
              {{ doc.name }}
              <small class="text-muted ml-2">{{ doc.typeName }}</small>
            </div>
            <b-form-checkbox
              :checked="isDocSelected(doc.id)"
              size="lg"
              @change="toggleDoc(doc.id)"
            />
          </b-list-group-item>
        </b-list-group>
        <div class="d-flex align-items-center justify-content-between border rounded px-3 py-2 mt-3"
             :class="renderOptions.mergeDocumentsIntoPdf ? 'border-primary bg-light' : 'border-light bg-white'">
          <div>
            <span class="font-weight-medium">Merge pages into PDF</span>
            <small class="d-block text-muted">Physically append document pages to the end of the PDF (Word files are converted automatically)</small>
          </div>
          <b-form-checkbox
            :checked="renderOptions.mergeDocumentsIntoPdf"
            switch
            size="lg"
            @change="toggleMerge"
            class="ml-2 flex-shrink-0"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import IncidentReportSectionToggles from './IncidentReportSectionToggles.vue'
import IncidentReportPeopleSelector from './IncidentReportPeopleSelector.vue'
import IncidentReportMediaSelector from './IncidentReportMediaSelector.vue'

export default {
  name: 'IncidentReportOptionsPanel',
  components: {
    IncidentReportSectionToggles,
    IncidentReportPeopleSelector,
    IncidentReportMediaSelector
  },
  props: {
    event: {
      type: Object,
      required: true
    },
    allImages: {
      type: Array,
      default: () => []
    },
    mediaLoading: {
      type: Boolean,
      default: false
    },
    renderOptions: {
      type: Object,
      required: true
    },
    involvedPeople: {
      type: Array,
      default: () => []
    },
    eventVideos: {
      type: Array,
      default: () => []
    },
    eventDocuments: {
      type: Array,
      default: () => []
    },
    eventExpenses: {
      type: Array,
      default: () => []
    },
    eventClaims: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    nonIncidentReportDocuments () {
      return this.eventDocuments || []
    }
  },
  methods: {
    onRenderOptionsUpdate (updated) {
      this.$emit('update:renderOptions', { ...this.renderOptions, ...updated })
    },
    onPeopleUpdate (ids) {
      this.$emit('update:renderOptions', { ...this.renderOptions, includedPeopleIds: ids })
    },
    onImagesUpdate (ids) {
      this.$emit('update:renderOptions', { ...this.renderOptions, includedImageIds: ids })
    },
    onVideosUpdate (ids) {
      this.$emit('update:renderOptions', { ...this.renderOptions, includedVideoIds: ids })
    },
    isClaimSelected (id) {
      return (this.renderOptions.includedInsuranceClaimIds || []).includes(id)
    },
    toggleClaim (id) {
      const current = [...(this.renderOptions.includedInsuranceClaimIds || [])]
      const idx = current.indexOf(id)
      if (idx >= 0) current.splice(idx, 1)
      else current.push(id)
      this.$emit('update:renderOptions', { ...this.renderOptions, includedInsuranceClaimIds: current })
    },
    selectAllClaims () {
      this.$emit('update:renderOptions', {
        ...this.renderOptions,
        includedInsuranceClaimIds: this.eventClaims.map(c => c.id)
      })
    },
    deselectAllClaims () {
      this.$emit('update:renderOptions', { ...this.renderOptions, includedInsuranceClaimIds: [] })
    },
    isDocSelected (id) {
      return (this.renderOptions.includedDocumentIds || []).includes(id)
    },
    toggleDoc (id) {
      const current = [...(this.renderOptions.includedDocumentIds || [])]
      const idx = current.indexOf(id)
      if (idx >= 0) current.splice(idx, 1)
      else current.push(id)
      this.$emit('update:renderOptions', { ...this.renderOptions, includedDocumentIds: current })
    },
    selectAllDocs () {
      this.$emit('update:renderOptions', {
        ...this.renderOptions,
        includedDocumentIds: this.nonIncidentReportDocuments.map(d => d.id)
      })
    },
    deselectAllDocs () {
      this.$emit('update:renderOptions', { ...this.renderOptions, includedDocumentIds: [] })
    },
    isExpenseSelected (id) {
      return (this.renderOptions.includedExpenseIds || []).includes(id)
    },
    toggleExpense (id) {
      const current = [...(this.renderOptions.includedExpenseIds || [])]
      const idx = current.indexOf(id)
      if (idx >= 0) current.splice(idx, 1)
      else current.push(id)
      this.$emit('update:renderOptions', { ...this.renderOptions, includedExpenseIds: current })
    },
    selectAllExpenses () {
      this.$emit('update:renderOptions', {
        ...this.renderOptions,
        includedExpenseIds: this.eventExpenses.map(e => e.id)
      })
    },
    deselectAllExpenses () {
      this.$emit('update:renderOptions', { ...this.renderOptions, includedExpenseIds: [] })
    },
    toggleMerge (val) {
      this.$emit('update:renderOptions', { ...this.renderOptions, mergeDocumentsIntoPdf: val })
    }
  }
}
</script>
